"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileCode2, ScanSearch, Layers } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

const stages = [
  {
    id: 1,
    text: "Reviewing previous analysis...",
    duration: 700,
    icon: ScanSearch,
    iconColor: "text-muted-foreground",
    bgColor: "bg-muted/50",
  },
  {
    id: 2,
    text: "Loading step history...",
    duration: 900,
    icon: Layers,
    iconColor: "text-muted-foreground",
    bgColor: "bg-muted/60",
  },
  {
    id: 3,
    text: "Analyzing earlier steps...",
    duration: 700,
    icon: FileCode2,
    iconColor: "text-foreground",
    bgColor: "bg-muted/70",
  },
];

export function GetCellDetailsLoading({ viewMode }: { viewMode?: string } = {}) {
  const chrome = getViewModeChrome(viewMode);
  const [currentStage, setCurrentStage] = useState(0);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    let totalDelay = 0;

    stages.forEach((stage, index) => {
      const timer = setTimeout(() => {
        setCurrentStage(index);
      }, totalDelay);

      totalDelay += stage.duration;
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  const currentStageData = stages[currentStage] || stages[0];
  const Icon = currentStageData.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="absolute top-0 left-0 h-[1px] bg-gradient-to-r from-transparent via-border to-transparent">
          <motion.div
            className="h-full bg-gradient-to-r from-foreground/0 via-foreground/80 to-foreground/0"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 2.3, ease: "linear" }}
          />
        </div>

        <div className="px-3 py-2 flex items-center gap-3 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2.5"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className={`w-7 h-7 rounded ${currentStageData.bgColor} flex items-center justify-center`}
              >
                <Icon
                  className={`w-4 h-4 ${chrome.iconMuted}`}
                  strokeWidth={2}
                />
              </motion.div>

              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {currentStageData.text}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
